import React, { useState } from 'react';
import { amDesignColors, amDesignTypography, amDesignComponents } from '../themes/amDesignSystem';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardMedia,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  GetApp as DownloadIcon,
  Star as StarIcon,
  Image as ImageIcon
} from '@mui/icons-material';

const ImageGallery = ({ open, onClose, mediaItems = [], profilePhoto = null, coverPhoto = null, businessName = "Unternehmen" }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setFullscreenOpen(true);
  };

  const handleDownload = (image) => {
    // Use proxy URLs for download to avoid rate limiting
    const url = image.proxySourceUrl || image.sourceUrl || image.proxyGoogleUrl || image.googleUrl;
    if (url) {
      window.open(url, '_blank');
    }
  };

  const getImageCategory = (image) => {
    if (profilePhoto && image.name === profilePhoto.name) {
      return { label: 'Profilbild', color: 'primary', icon: <StarIcon fontSize="small" /> };
    }
    if (coverPhoto && image.name === coverPhoto.name) {
      return { label: 'Titelbild', color: 'secondary', icon: <ImageIcon fontSize="small" /> };
    }
    
    const category = image.locationAssociation?.category;
    switch (category) {
      case 'EXTERIOR':
        return { label: 'Außenansicht', color: 'success' };
      case 'INTERIOR':
        return { label: 'Innenraum', color: 'info' };
      case 'PRODUCT':
        return { label: 'Produkt', color: 'warning' };
      case 'TEAM':
        return { label: 'Team', color: 'error' };
      case 'ADDITIONAL':
        return { label: 'Zusätzlich', color: 'default' };
      default:
        return { label: 'Foto', color: 'default' };
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, maxHeight: '90vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ImageIcon />
            <Box>
              <Typography variant="h6" fontWeight="bold">
                Bilder-Galerie
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {businessName} • {mediaItems.length} Bilder
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          {mediaItems.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', bgcolor: '#f5f5f5' }}>
              <ImageIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Keine Bilder verfügbar
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Es wurden noch keine Bilder für dieses Unternehmen hochgeladen.
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {mediaItems.map((image, index) => {
                const category = getImageCategory(image);
                // Use proxy URLs to avoid Google rate limiting, fallback to original URLs
                const thumbnailUrl = image.proxyThumbnailUrl || image.thumbnailUrl || image.proxyGoogleUrl || image.googleUrl;
                const isProfilePhoto = profilePhoto && image.name === profilePhoto.name;
                const isCoverPhoto = coverPhoto && image.name === coverPhoto.name;
                
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={image.name || index}>
                    <Card 
                      sx={{ 
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        border: (isProfilePhoto || isCoverPhoto) ? '2px solid' : '1px solid',
                        borderColor: isProfilePhoto ? 'primary.main' : isCoverPhoto ? 'secondary.main' : 'divider',
                        '&:hover': {
                          transform: 'scale(1.02)',
                          boxShadow: 3
                        }
                      }}
                      onClick={() => handleImageClick(image)}
                    >
                      <CardMedia
                        component="img"
                        height="200"
                        image={thumbnailUrl}
                        alt={`Bild ${index + 1}`}
                        sx={{
                          objectFit: 'cover',
                          bgcolor: '#f5f5f5'
                        }}
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjVmNWY1Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPkJpbGQgbmljaHQgdmVyZsO8Z2JhcjwvdGV4dD48L3N2Zz4=';
                        }}
                      />
                      
                      {/* Category Chip */}
                      <Box sx={{ position: 'absolute', top: 8, left: 8 }}>
                        <Chip
                          icon={category.icon}
                          label={category.label}
                          size="small"
                          color={category.color}
                          sx={{
                            fontSize: '0.75rem',
                            height: 24,
                            bgcolor: 'rgba(255,255,255,0.9)',
                            backdropFilter: 'blur(4px)'
                          }}
                        />
                      </Box>

                      {/* Download Button */}
                      <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                        <Tooltip title="Bild öffnen">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownload(image);
                            }}
                            sx={{
                              bgcolor: 'rgba(255,255,255,0.9)',
                              backdropFilter: 'blur(4px)',
                              '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
                            }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>

                      {/* Image Dimensions */}
                      {image.dimensions && (
                        <Box sx={{ position: 'absolute', bottom: 8, right: 8 }}>
                          <Chip
                            label={`${image.dimensions.widthPixels}×${image.dimensions.heightPixels}`}
                            size="small"
                            sx={{
                              fontSize: '0.7rem',
                              height: 20,
                              bgcolor: 'rgba(0,0,0,0.7)',
                              color: amDesignColors.white,
                              backdropFilter: 'blur(4px)'
                            }}
                          />
                        </Box>
                      )}
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {mediaItems.length} Bilder • Klicken Sie auf ein Bild für Vollansicht
          </Typography>
          <Button onClick={onClose} variant="outlined">
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fullscreen Image Dialog */}
      <Dialog
        open={fullscreenOpen}
        onClose={() => setFullscreenOpen(false)}
        maxWidth="xl"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            bgcolor: 'rgba(0,0,0,0.9)',
            borderRadius: 0,
            maxHeight: '100vh',
            margin: 0
          }
        }}
      >
        {selectedImage && (
          <>
            <DialogTitle sx={{ color: amDesignColors.white, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                {getImageCategory(selectedImage).label}
              </Typography>
              <IconButton onClick={() => setFullscreenOpen(false)} sx={{ color: amDesignColors.white }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 0 }}>
              <img
                src={selectedImage.proxySourceUrl || selectedImage.sourceUrl || selectedImage.proxyGoogleUrl || selectedImage.googleUrl}
                alt="Vollbild"
                style={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  // Fallback to thumbnail if full size fails
                  e.target.src = selectedImage.proxyThumbnailUrl || selectedImage.thumbnailUrl;
                }}
              />
            </DialogContent>
          </>
        )}
      </Dialog>
    </>
  );
};

export default ImageGallery;